<template>
  <div class="detail-page-header" :class="headerClasses" :style="headerStyle">
    <div class="header-content">
      <!-- Left Side: Back Button + Icon + Title/Subtitle -->
      <div class="header-left">
        <!-- Back Button -->
        <q-btn
          v-if="backRoute"
          flat
          round
          dense
          icon="arrow_back"
          color="white"
          size="lg"
          class="back-btn"
          @click="handleBack"
        >
          <q-tooltip>{{ backLabel }}</q-tooltip>
        </q-btn>

        <!-- Icon + Title/Subtitle Section -->
        <div class="title-section">
          <!-- Optional Avatar Icon -->
          <q-avatar
            v-if="icon"
            :icon="icon"
            size="48px"
            color="white"
            text-color="primary"
            class="header-icon"
          />

          <div class="page-info">
            <h1 class="page-title text-h4 text-weight-bold text-white">
              {{ title }}
            </h1>
            <p v-if="subtitle" class="page-subtitle text-body1 text-blue-2">
              {{ subtitle }}
            </p>
          </div>
        </div>
      </div>

      <!-- Middle: Inline Stats (hidden on small screens) -->
      <div
        v-if="stats.length > 0"
        class="header-stats gt-sm"
      >
        <div
          v-for="(stat, index) in stats"
          :key="index"
          class="stat-item"
        >
          <q-icon
            v-if="stat.icon"
            :name="stat.icon"
            size="20px"
            class="stat-icon"
          />
          <span class="stat-value">{{ stat.value }}</span>
        </div>
      </div>

      <!-- Right Side: Status + Actions -->
      <div class="header-right">
        <!-- Status Badge -->
        <q-chip
          v-if="status"
          :color="status.color || 'grey-7'"
          :text-color="getStatusTextColor(status.color)"
          :label="status.label"
          class="status-chip"
        />

        <!-- Action Buttons -->
        <div class="header-actions">
          <slot name="actions">
            <q-btn-group v-if="actions.length > 0">
              <q-btn
                v-for="(action, index) in actions"
                :key="index"
                :color="action.color || 'white'"
                :text-color="action.textColor || 'primary'"
                :icon="action.icon"
                :label="action.label"
                :flat="action.flat"
                :round="action.round"
                :size="action.size || 'lg'"
                :loading="action.loading"
                :class="action.class"
                @click="action.menu ? null : handleActionClick(action)"
              >
                <q-tooltip v-if="action.tooltip">{{ action.tooltip }}</q-tooltip>

                <!-- Menu support -->
                <q-menu v-if="action.menu" anchor="bottom right" self="top right">
                  <q-list dense style="min-width: 200px">
                    <template v-for="(menuItem, menuIndex) in action.menu" :key="menuIndex">
                      <!-- Separator -->
                      <q-separator v-if="menuItem.separator" />

                      <!-- Menu item with submenu -->
                      <q-item v-else-if="menuItem.submenu" clickable>
                        <q-item-section v-if="menuItem.icon" avatar>
                          <q-icon :name="menuItem.icon" />
                        </q-item-section>
                        <q-item-section>{{ menuItem.label }}</q-item-section>
                        <q-item-section side>
                          <q-icon name="chevron_right" />
                        </q-item-section>

                        <!-- Submenu -->
                        <q-menu anchor="top end" self="top start">
                          <q-list dense style="min-width: 200px">
                            <q-item
                              v-for="(subItem, subIndex) in menuItem.submenu"
                              :key="subIndex"
                              v-close-popup
                              clickable
                              @click="subItem.onClick ? subItem.onClick() : null"
                            >
                              <q-item-section v-if="subItem.icon" avatar>
                                <q-icon :name="subItem.icon" />
                              </q-item-section>
                              <q-item-section>{{ subItem.label }}</q-item-section>
                            </q-item>
                          </q-list>
                        </q-menu>
                      </q-item>

                      <!-- Regular menu item -->
                      <q-item
                        v-else
                        v-close-popup
                        clickable
                        @click="menuItem.onClick ? menuItem.onClick() : null"
                      >
                        <q-item-section v-if="menuItem.icon" avatar>
                          <q-icon :name="menuItem.icon" />
                        </q-item-section>
                        <q-item-section>{{ menuItem.label }}</q-item-section>
                      </q-item>
                    </template>
                  </q-list>
                </q-menu>
              </q-btn>
            </q-btn-group>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed } from 'vue';
  import { useRouter } from 'vue-router';

  const props = defineProps({
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
    backRoute: {
      type: String,
      default: '',
    },
    backLabel: {
      type: String,
      default: 'Back',
    },
    status: {
      type: Object,
      default: null,
    // Format: { label: 'Pending', color: 'orange' }
    },
    stats: {
      type: Array,
      default: () => [],
    // Format: [{ value: '5', label: 'Items', icon: 'list' }]
    },
    actions: {
      type: Array,
      default: () => [],
    // Format: [{ icon: 'edit', label: 'Edit', onClick: fn, color: 'white', textColor: 'primary' }]
    },
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'white', 'custom'].includes(value),
    },
    customGradient: {
      type: String,
      default: '',
    },
  });

  const emit = defineEmits(['back', 'action-click']);

  const router = useRouter();

  const headerClasses = computed(() => ({
    'detail-page-header--primary': props.variant === 'primary',
    'detail-page-header--white': props.variant === 'white',
    'detail-page-header--custom': props.variant === 'custom',
  }));

  const headerStyle = computed(() => {
    if (props.variant === 'custom' && props.customGradient) {
      return { background: props.customGradient };
    }
    return {};
  });

  const handleBack = () => {
    if (props.backRoute) {
      router.push(props.backRoute);
    }
    emit('back');
  };

  const handleActionClick = (action) => {
    if (action.onClick) {
      action.onClick();
    }
    emit('action-click', action);
  };

  const getStatusTextColor = (color) => {
    // Dark colors need white text, light colors need dark text
    const darkColors = ['red', 'red-10', 'red-9', 'red-8', 'red-7', 'red-6',
                        'pink', 'pink-10', 'pink-9', 'pink-8', 'pink-7', 'pink-6',
                        'purple', 'purple-10', 'purple-9', 'purple-8', 'purple-7', 'purple-6',
                        'deep-purple', 'deep-purple-10', 'deep-purple-9', 'deep-purple-8', 'deep-purple-7', 'deep-purple-6',
                        'indigo', 'indigo-10', 'indigo-9', 'indigo-8', 'indigo-7', 'indigo-6',
                        'blue', 'blue-10', 'blue-9', 'blue-8', 'blue-7', 'blue-6',
                        'light-blue', 'light-blue-10', 'light-blue-9', 'light-blue-8', 'light-blue-7', 'light-blue-6',
                        'cyan', 'cyan-10', 'cyan-9', 'cyan-8', 'cyan-7', 'cyan-6',
                        'teal', 'teal-10', 'teal-9', 'teal-8', 'teal-7', 'teal-6',
                        'green', 'green-10', 'green-9', 'green-8', 'green-7', 'green-6',
                        'light-green', 'light-green-10', 'light-green-9', 'light-green-8', 'light-green-7', 'light-green-6',
                        'brown', 'brown-10', 'brown-9', 'brown-8', 'brown-7', 'brown-6',
                        'blue-grey', 'blue-grey-10', 'blue-grey-9', 'blue-grey-8', 'blue-grey-7', 'blue-grey-6',
                        'grey-10', 'grey-9', 'grey-8', 'grey-7', 'grey-6',
                        'primary', 'secondary', 'accent', 'dark', 'positive', 'negative', 'info', 'warning'];

    return darkColors.includes(color) ? 'white' : 'black';
  };
</script>

<style lang="scss" scoped>
/* Main Header */
.detail-page-header {
  padding: 24px;
  border-radius: 0;
}

.detail-page-header--primary {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}

.detail-page-header--white {
  background: white;
  border-bottom: 1px solid #e9ecef;
}

.detail-page-header--white .page-title,
.detail-page-header--white .page-subtitle,
.detail-page-header--white .stat-value {
  color: #1d1d1d !important;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
}

.header-left {
  flex: 1;
  display: flex;
  gap: 16px;
  align-items: center;
}

.back-btn {
  flex-shrink: 0;
}

.title-section {
  display: flex;
  gap: 16px;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.header-icon {
  flex-shrink: 0;
}

.page-info {
  flex: 1;
  min-width: 0;
}

.page-title {
  margin: 0;
  line-height: 1.2;
}

.page-subtitle {
  margin: 0;
}

.header-right {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 16px;
}

.status-chip {
  font-weight: 600;
  font-size: 0.875rem;
}

.header-actions {
  flex-shrink: 0;
}

/* Inline Stats */
.header-stats {
  display: flex;
  gap: 24px;
  margin: 0 24px;
  flex-shrink: 0;
  align-items: center;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-icon {
  color: white;
  flex-shrink: 0;
}

.stat-value {
  font-size: 1.125rem;
  font-weight: 600;
  color: white !important;
  line-height: 1;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
}

/* Responsive */
@media (max-width: 1024px) {
  .header-content {
    flex-wrap: wrap;
  }

  .header-left {
    flex: 1 1 100%;
    max-width: 100%;
  }

  .header-right {
    flex: 1 1 100%;
    justify-content: flex-end;
    margin-top: 16px;
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .header-left {
    flex-direction: column;
    width: 100%;
    align-items: flex-start;
  }

  .title-section {
    width: 100%;
  }

  .header-right {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    margin-top: 0;
  }

  .status-chip {
    margin-bottom: 8px;
  }
}

@media (max-width: 480px) {
  .detail-page-header {
    padding: 16px;
  }

  .title-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .page-title {
    font-size: 1.5rem;
  }
}
</style>